/**
 * AuthenticatedApp - Wrapper that initializes app data after authentication
 * Only renders children after app data is fully loaded
 */
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { useSignalEffect } from '@preact/signals';
import { isAuthenticated } from '../../state/userState.js';
import { initializeApp } from '../../utils/appInitializer.js';
import { initPageStateManager } from '../../utils/pageStateManager.js';
import { LoadingScreen } from './LoadingScreen.jsx';
import { tourStates, startTour } from '../../tours/tourState.js';
import { ionHidden, chatAccess } from '../../state/chatStore.js';

let hasInitialized = false;

export function AuthenticatedApp({ children }) {
  const [isLoading, setIsLoading] = useState(true);

  // Use useSignalEffect for proper signal reactivity
  useSignalEffect(() => {
    const authenticated = isAuthenticated.value;
    
    // Only initialize once we know user is authenticated
    if (!authenticated) {
      console.log('[AuthenticatedApp] User not authenticated - waiting...');
      setIsLoading(false);
      return;
    }

    // Skip if already initialized
    if (hasInitialized) {
      console.log('[AuthenticatedApp] Already initialized');
      setIsLoading(false);
      return;
    }

    console.log('[AuthenticatedApp] User authenticated - initializing app data...');

    async function initData() {
      try {
        initPageStateManager();
        await initializeApp();
        hasInitialized = true;
        console.log('[AuthenticatedApp] ✓ App data initialized');

        // Engagement-time heartbeat for admin analytics (LF3).
        // Lazy-loaded so the bundle for non-authenticated routes stays slim.
        import('../../services/engagementService.js').then((m) => m.startHeartbeat());

        // Auto-trigger the first-run tour for brand new users (no tour history
        // at all) — ion-intro for Ion-access users, welcome otherwise. On
        // prod (ionFeatureEnabled===false) ionHidden is true, so this always
        // falls back to welcome there.
        // Small delay to let tourSyncService hydration from backend settle
        setTimeout(() => {
          if (Object.keys(tourStates.value).length === 0) {
            const ionReady = !ionHidden.value && chatAccess.value.available;
            console.log(`[AuthenticatedApp] New user detected — starting ${ionReady ? 'ion-intro' : 'Welcome'} Tour`);
            startTour(ionReady ? 'ion-intro' : 'welcome');
          }
        }, 1500);
      } catch (err) {
        console.error('[AuthenticatedApp] ✗ Initialization failed:', err);
      } finally {
        setIsLoading(false);
      }
    }

    initData();
  });

  // Show loading screen while initializing
  if (isLoading) {
    return <LoadingScreen />;
  }

  return children;
}
